import { sqliteTable, text, integer, uniqueIndex, index } from "drizzle-orm/sqlite-core";
import { sql } from "drizzle-orm";

// ============================================================================
// numbering_series — Auto-naming: KB-KCR-2026-00001
//
// A5 (#353) extended this table with `isDefault` + `seriesName` columns to
// support multiple series per (module, doctype, companyId) from the Doctype
// Admin naming tab. Exactly one row per triple has `isDefault = 1`, enforced
// by the partial unique index below and by `setDefaultSeries()` in the
// service layer (which flips the flag atomically).
//
// Legacy single-row editors (e.g. /settings/numbering) still work because:
//   - `isDefault` DEFAULT 1 means existing rows become "the default" on
//     migration.
//   - `seriesName` is nullable — UI falls back to the prefix string.
//   - `generateNextNumber()` picks the row where `isDefault = 1` via
//     `findSeries()`, which is deterministic.
// ============================================================================
export const numberingSeries = sqliteTable(
  "core_numbering_series",
  {
    id: text("id").primaryKey(), // ULID
    module: text("module").notNull(),
    doctype: text("doctype").notNull(),
    /** Human label (e.g. "Standard Invoice"); nullable for legacy rows. */
    seriesName: text("series_name"),
    prefix: text("prefix").notNull(), // e.g., "RB-{YYYY}-#####", "{A}{A}{0}{0}{0}{0}"
    currentValue: integer("current_value").notNull().default(0),
    companyId: text("company_id"), // optional — null = global series
    resetEvery: text("reset_every").notNull().default("yearly"), // "never" | "yearly" | "monthly"
    lastResetYear: integer("last_reset_year"), // track year for yearly reset
    lastResetMonth: integer("last_reset_month"), // track month for monthly reset
    autoGenerate: integer("auto_generate").notNull().default(1),
    isActive: integer("is_active").notNull().default(1),
    /** 1 = default series picked by generateNextNumber. Exactly one per triple. */
    isDefault: integer("is_default").notNull().default(1),
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
  },
  (table) => [
    // Partial UNIQUE — at most one default row per (module, doctype, company_id).
    // Matches the partial index in migration 003. Drizzle's `.where()` on an
    // index emits the WHERE predicate in the generated DDL.
    uniqueIndex("uq_numbering_series_default")
      .on(table.module, table.doctype, table.companyId)
      .where(sql`${table.isDefault} = 1`),
    // Lookup helper for "list all series for a doctype in this company".
    index("idx_numbering_series_doctype_company").on(
      table.module,
      table.doctype,
      table.companyId,
    ),
  ],
);

// Type exports
export type NumberingSeries = typeof numberingSeries.$inferSelect;
export type NewNumberingSeries = typeof numberingSeries.$inferInsert;
